import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  ReferenceLine,
  ReferenceDot
} from 'recharts';
import { 
  TrendingUp, 
  BarChart3, 
  Activity, 
  Zap, 
  PlayCircle,
  Download,
  Network
} from 'lucide-react';
import { QueueingSystemParams, ChartType } from '../types';
import { QueueingCalculator } from '../utils/queueingCalculations';
import { DiagramTab } from './DiagramTab';

interface ChartsProps {
  params: QueueingSystemParams;
}

const CustomTooltip = ({ active, payload, label, labelFormatter }: any) => {
  if (!active || !payload || !payload.length) return null;

  // Format the label based on chart type
  const formatLabel = (rawLabel: any) => {
    if (typeof rawLabel === 'number') {
      // Check if this is a utilization value (between 0 and 1)
      if (rawLabel >= 0 && rawLabel <= 1) {
        return `Utilization: ${(rawLabel * 100).toFixed(1)}%`;
      }
      return rawLabel.toFixed(1);
    }
    return rawLabel;
  };

  // Format payload values
  const formatValue = (entry: any) => {
    if (entry.dataKey === 'y') {
      return `Queue Time: ${entry.value.toFixed(2)} min`;
    }
    if (entry.name && entry.name.includes('Time')) {
      return `${entry.name}: ${entry.value.toFixed(2)} min`;
    }
    if (entry.name && (entry.name.includes('utilization') || entry.name.includes('Utilization'))) {
      return `${entry.name}: ${(entry.value * 100).toFixed(1)}%`;
    }
    return `${entry.name || entry.dataKey}: ${typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}`;
  };

  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg p-3 shadow-xl">
      <div className="text-sm text-gray-300 mb-1">
        {formatLabel(label)}
      </div>
      {payload
        .filter((entry: any) => entry.dataKey !== 'x') // Remove duplicate x-axis values
        .filter((entry: any, index: number, array: any[]) => {
          // Remove duplicates based on dataKey - keep only the first occurrence
          return array.findIndex(item => item.dataKey === entry.dataKey) === index;
        })
        .map((entry: any, index: number) => (
          <div key={index} className="text-sm" style={{ color: entry.color }}>
            {formatValue(entry)}
          </div>
        ))}
    </div>
  );
};

export const Charts: React.FC<ChartsProps> = ({ params }) => {
  const [activeChart, setActiveChart] = useState<ChartType>('diagram');

  // Calculate current system metrics for reference points
  const currentMetrics = useMemo(() => {
    return QueueingCalculator.calculateMetrics(params);
  }, [params]);

  // Generate chart data
  const utilizationData = useMemo(() => {
    const curveData = QueueingCalculator.generateUtilizationCurve(params);
    // Add current system point to the data
    const currentPoint = {
      x: currentMetrics.utilization,
      y: currentMetrics.queueTime,
      isCurrentSystem: true
    };
    return [...curveData, currentPoint].sort((a, b) => a.x - b.x);
  }, [params, currentMetrics]);

  const variabilityData = useMemo(() => {
    const curves = QueueingCalculator.generateVariabilityCurves(params);
    const maxLength = Math.max(...Object.values(curves).map(curve => curve.length));
    
    const data = [];
    for (let i = 0; i < maxLength; i++) {
      const point: any = {};
      Object.entries(curves).forEach(([label, curve]) => {
        if (curve[i]) {
          point.utilization = curve[i].x;
          point[label] = curve[i].y;
        }
      });
      if (point.utilization !== undefined) data.push(point);
    }
    return data;
  }, [params]);

  const poolingData = useMemo(() => {
    if (params.servers <= 1) return [];
    
    const comparison = QueueingCalculator.calculatePoolingComparison(params);
    return [
      {
        type: 'Dedicated Queues',
        queueTime: comparison.dedicated.queueTime,
        cycleTime: comparison.dedicated.cycleTime,
        totalWIP: comparison.dedicated.totalWIP,
        utilization: comparison.dedicated.utilization
      },
      {
        type: 'Pooled Queue',
        queueTime: comparison.pooled.queueTime,
        cycleTime: comparison.pooled.cycleTime,
        totalWIP: comparison.pooled.wip,
        utilization: comparison.pooled.utilization
      }
    ];
  }, [params]);

  const sensitivityData = useMemo(() => {
    // Calculate safe arrival rate range that keeps utilization under 95%
    const maxSafeArrivalRate = (params.serviceRate * params.servers * 0.95);
    const safeMax = Math.min(params.arrivalRate * 1.5, maxSafeArrivalRate);
    
    const analysis = QueueingCalculator.performSensitivityAnalysis(
      params,
      'arrivalRate',
      { min: params.arrivalRate * 0.5, max: safeMax, steps: 20 }
    );
    
    // Filter out any points where utilization >= 95% as extra safety
    const safeData = analysis.values
      .map((value, index) => ({
        arrivalRate: value,
        queueTime: analysis.queueTimes[index],
        cycleTime: analysis.cycleTimes[index],
        utilization: analysis.utilizations[index]
      }))
      .filter(point => point.utilization < 0.95);
    
    return safeData;
  }, [params]);

  const simulationData = useMemo(() => {
    const results = QueueingCalculator.runSimulation(params, 1000, 100);
    return results.filter((_, index) => index % 10 === 0).slice(0, 100); // Sample every 10th point
  }, [params]);

  const tabs = [
    { id: 'diagram', label: 'System Diagram', icon: <Network className="w-4 h-4" /> },
    { id: 'utilization', label: 'Utilization Impact', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'variability', label: 'Variability Analysis', icon: <Activity className="w-4 h-4" /> },
    { id: 'pooling', label: 'Server Pooling', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'sensitivity', label: 'Sensitivity', icon: <Zap className="w-4 h-4" /> },
    { id: 'simulation', label: 'Simulation', icon: <PlayCircle className="w-4 h-4" /> }
  ];

  const handleExportCharts = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      systemParameters: params,
      currentMetrics: currentMetrics,
      chartData: {
        utilization: utilizationData,
        variability: variabilityData,
        pooling: poolingData,
        sensitivity: sensitivityData,
        simulation: simulationData
      },
      metadata: {
        exportedFrom: 'IKUTU - Theory Analysis Platform',
        version: '1.0.0',
        description: 'Complete queueing system analysis data export'
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `queueing-analysis-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderChart = () => {
    switch (activeChart) {
      case 'diagram':
        return <DiagramTab params={params} />;
        
      case 'utilization':
        return (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={utilizationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="x" 
                  stroke="#9CA3AF"
                  tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                  domain={currentMetrics.utilization < 0.5 ? [0, 1] : ['dataMin', 'dataMax']}
                />
                <YAxis stroke="#9CA3AF" />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="y"
                  stroke="#3B82F6"
                  fill="url(#utilizationGradient)"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="y"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={(props: any) => {
                    const { payload } = props;
                    if (payload?.isCurrentSystem) {
                      return (
                        <circle
                          cx={props.cx}
                          cy={props.cy}
                          r={8}
                          fill="#EF4444"
                          stroke="#FFFFFF"
                          strokeWidth={3}
                        />
                      );
                    }
                    return <circle cx={props.cx} cy={props.cy} r={0} fill="transparent" />;
                  }}
                  activeDot={false}
                />
                {/* Current System Reference Line */}
                <ReferenceLine 
                  x={currentMetrics.utilization} 
                  stroke="#FFFFFF" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  label={{ value: "Current System", position: "top", fill: "#FFFFFF", fontSize: 12, fontWeight: "bold" }}
                />
                <defs>
                  <linearGradient id="utilizationGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'variability':
        const colors = ['#10B981', '#F59E0B', '#3B82F6', '#EF4444'];
        return (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={variabilityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="utilization" 
                  stroke="#9CA3AF"
                  tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                  domain={currentMetrics.utilization < 0.5 ? [0, 1] : ['dataMin', 'dataMax']}
                />
                <YAxis stroke="#9CA3AF" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {Object.keys(variabilityData[0] || {})
                  .filter(key => key !== 'utilization')
                  .map((key, index) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={colors[index % colors.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                {/* Current System Point */}
                <ReferenceDot 
                  x={currentMetrics.utilization} 
                  y={currentMetrics.queueTime} 
                  r={8} 
                  fill="#EF4444" 
                  stroke="#FFFFFF" 
                  strokeWidth={3}
                />
                <ReferenceLine 
                  x={currentMetrics.utilization} 
                  stroke="#EF4444" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  label={{ value: "Your System", position: "bottom", fill: "#EF4444", fontSize: 12 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'pooling':
        return (
          <div className="h-96">
            {poolingData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={poolingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="type" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="queueTime" fill="#3B82F6" name="Queue Time" />
                  <Bar dataKey="totalWIP" fill="#10B981" name="Total WIP" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Server pooling comparison requires more than one server (m &gt; 1)</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'sensitivity':
        return (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sensitivityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="arrivalRate" 
                  stroke="#9CA3AF"
                  tickFormatter={(value) => value.toFixed(1)}
                />
                <YAxis stroke="#9CA3AF" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="queueTime"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  name="Queue Time"
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="utilization"
                  stroke="#EF4444"
                  strokeWidth={2}
                  name="Utilization"
                  dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
                />
                {/* Current Arrival Rate */}
                <ReferenceLine 
                  x={params.arrivalRate} 
                  stroke="#10B981" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  label={{ value: "Current λ", position: "top", fill: "#10B981", fontSize: 12 }}
                />
                <ReferenceDot 
                  x={params.arrivalRate} 
                  y={currentMetrics.queueTime} 
                  r={6} 
                  fill="#10B981" 
                  stroke="#FFFFFF" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'simulation':
        return (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart data={simulationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="time" 
                  stroke="#9CA3AF" 
                  name="Time"
                  tickFormatter={(value) => value.toFixed(1)}
                />
                <YAxis dataKey="queueLength" stroke="#9CA3AF" name="Queue Length" />
                <Tooltip content={<CustomTooltip />} />
                <Scatter dataKey="queueLength" fill="#3B82F6" />
                {/* Theoretical average queue length */}
                <ReferenceLine 
                  y={currentMetrics.wipQueue} 
                  stroke="#EF4444" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  label={{ value: `Avg: ${currentMetrics.wipQueue.toFixed(1)}`, position: "insideTopRight", fill: "#EF4444", fontSize: 12 }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="chart-container">
      {/* Chart Tabs */}
      <div className="flex flex-wrap border-b border-dark-600 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveChart(tab.id as ChartType)}
            className={`tab-button flex items-center space-x-2 ${
              activeChart === tab.id ? 'active' : ''
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
        <div className="ml-auto">
          <button 
            onClick={handleExportCharts}
            className="btn-secondary text-xs px-3 py-1 flex items-center space-x-1 hover:bg-dark-600 transition-colors"
            title="Export all chart data to JSON file"
          >
            <Download className="w-3 h-3" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Chart Content */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-200 mb-2">
          {tabs.find(tab => tab.id === activeChart)?.label}
        </h3>
        <p className="text-sm text-gray-400 mb-6">
          {activeChart === 'diagram' && 'Visual representation of queueing system flow with real-time KPI values'}
          {activeChart === 'utilization' && 'Shows how queue time increases exponentially with system utilization'}
          {activeChart === 'variability' && 'Compares queue times across different variability scenarios'}
          {activeChart === 'pooling' && 'Compares dedicated queues vs. pooled queue performance'}
          {activeChart === 'sensitivity' && 'Analyzes system sensitivity to arrival rate changes'}
          {activeChart === 'simulation' && 'Discrete event simulation results over time'}
        </p>
      </div>

      {renderChart()}

      {/* Chart Insights */}
      <div className="mt-6 p-4 bg-dark-900 rounded-lg border border-dark-600">
        <h4 className="font-medium text-gray-200 mb-2">Key Insights</h4>
        <div className="text-sm text-gray-400 space-y-1">
          {activeChart === 'diagram' && (
            <>
              <p>• Visual flow diagram shows queue dynamics with animated elements</p>
              <p>• Color-coded servers indicate utilization levels (green &lt; 60%, yellow &lt; 80%, red ≥ 80%)</p>
              <p>• Compare pooled vs dedicated queue configurations to understand performance differences</p>
              <p>• Live KPI values update automatically as you adjust system parameters</p>
            </>
          )}
          {activeChart === 'utilization' && (
            <>
              <p>• Queue time grows exponentially as utilization approaches 100%</p>
              <p>• Operating above 85% utilization typically leads to poor performance</p>
              <p>• <span className="text-red-400">Red point</span> and <span className="text-white">white line</span> show your current system position: {QueueingCalculator.formatPercentage(currentMetrics.utilization)} utilization</p>
            </>
          )}
          {activeChart === 'variability' && (
            <>
              <p>• Higher variability (CV) significantly increases queue times</p>
              <p>• M/M/1 systems (CV=1) are baseline for comparison</p>
              <p>• <span className="text-red-400">Red point</span> shows your system among different variability scenarios</p>
              <p>• Reducing variability is often more effective than adding capacity</p>
            </>
          )}
          {activeChart === 'pooling' && poolingData.length > 0 && (
            <>
              <p>• Pooled queues typically outperform dedicated queues</p>
              <p>• Benefits increase with higher utilization and variability</p>
              <p>• Resource sharing improves overall system efficiency</p>
            </>
          )}
          {activeChart === 'sensitivity' && (
            <>
              <p>• Shows system response to demand changes</p>
              <p>• <span className="text-green-400">Green line</span> shows your current arrival rate (λ = {params.arrivalRate.toFixed(1)})</p>
              <p>• Helps identify operational boundaries</p>
              <p>• Critical for capacity planning decisions</p>
            </>
          )}
          {activeChart === 'simulation' && (
            <>
              <p>• Validates analytical results through simulation</p>
              <p>• <span className="text-red-400">Red line</span> shows theoretical average queue length ({currentMetrics.wipQueue.toFixed(1)} items)</p>
              <p>• Shows system behavior over time</p>
              <p>• Captures dynamic effects and variability</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};